import { HttpEvent, HttpHandlerFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BizError } from '@zopup/zop-types';

/**
 * Enhanced HTTP Error Response that preserves HTTP status codes
 * while providing standardized error information for UI consumption
 */
export interface EnhancedHttpErrorResponse extends HttpErrorResponse {
  bizErrors: BizError[];
  userMessage: string;
  standardizedResponse: {
    isSuccess: false;
    result: null;
    errors: BizError[];
  };
}

export const globalErrorInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Extract standardized error information
      const bizErrors = extractBizErrors(error);
      const standardizedResponse = {
        isSuccess: false as const,
        result: null,
        errors: bizErrors
      };

      // Create user-friendly message
      const userMessage = bizErrors[0]?.description || bizErrors[0]?.message || 'An error occurred';

      // Log the error for debugging (can be disabled in production)
      if (!isProduction()) {
        console.error('HTTP Error intercepted:', {
          url: req.url,
          method: req.method,
          status: error.status,
          statusText: error.statusText,
          bizErrors,
          originalError: error
        });
      }

      // Enhance the error object with standardized information
      // while preserving the original HTTP status code
      const enhancedError: EnhancedHttpErrorResponse = Object.assign(error, {
        bizErrors,
        userMessage,
        standardizedResponse
      });

      // Throw the enhanced error - this preserves HTTP status codes
      // Components can access both the status code AND the standardized error info
      return throwError(() => enhancedError);
    })
  );
};

/**
 * Check if running in production environment
 */
function isProduction(): boolean {
  // This can be enhanced to check actual environment
  return false; // Enable console logging by default for now
}

/**
 * Extract business errors from HTTP error response
 * Supports multiple error formats from the backend
 */
function extractBizErrors(error: HttpErrorResponse): BizError[] {
  const bizErrors: BizError[] = [];

  // INTEGRATION-GUIDE §I1/§J2/§O1/§T1: the backend now returns structured BizErrors on
  // 409 (immutability gates, version mismatch, barcode in-use) and other non-400 statuses.
  // Prefer the envelope's errors array for ANY status so the verbatim, user-actionable
  // messages (including recovery paths) are never replaced by a generic HTTP label.
  if (error.error?.errors && Array.isArray(error.error.errors) && error.error.errors.length > 0) {
    return error.error.errors;
  }

  // Handle specific HTTP error cases
  if (error.status === 400) {
    // Fallback for 400 without errors array
    bizErrors.push({
      errorCode: 400,
      message: 'Bad Request',
      description: extractSingleErrorMessage(error) || 'Invalid request. Please check your input.'
    });
  } else if (error.status === 401) {
    // Unauthorized
    bizErrors.push({
      errorCode: 401,
      message: 'Unauthorized',
      description: 'Unauthorized access. Please login again.'
    });
  } else if (error.status === 403) {
    // Forbidden
    bizErrors.push({
      errorCode: 403,
      message: 'Forbidden',
      description: 'Access forbidden. You do not have permission to perform this action.'
    });
  } else if (error.status === 404) {
    // Resource not found
    bizErrors.push({
      errorCode: 404,
      message: 'Not Found',
      description: extractSingleErrorMessage(error) || 'The requested resource was not found.'
    });
  } else if (error.status === 500) {
    // Internal server error
    bizErrors.push({
      errorCode: 500,
      message: 'Internal Server Error',
      description: 'An internal server error occurred. Please try again later.'
    });
  } else if (error.status === 0) {
    // Network error (no internet connection or CORS issue)
    bizErrors.push({
      errorCode: 0,
      message: 'Network Error',
      description: 'Unable to connect to the server. Please check your internet connection.'
    });
  } else {
    // Other HTTP errors (502, 503, etc.)
    bizErrors.push({
      errorCode: error.status,
      message: `HTTP ${error.status} Error`,
      description: extractSingleErrorMessage(error) || `An error occurred (${error.statusText}).`
    });
  }

  return bizErrors;
}

/**
 * Extract a single error message from various possible error response formats
 * Tries multiple common locations where backend APIs might place error messages
 */
function extractSingleErrorMessage(error: HttpErrorResponse): string | null {
  // Try: error.error.message (most common)
  if (error.error?.message) {
    return error.error.message;
  }

  // Try: error.error.error.message (nested error)
  if (error.error?.error?.message) {
    return error.error.error.message;
  }

  // Try: error.error as string
  if (typeof error.error === 'string' && error.error.trim()) {
    return error.error;
  }

  // Try: error.message (HttpErrorResponse built-in message)
  if (error.message) {
    return error.message;
  }

  return null;
}
